"use client"

import { useForm } from "@inertiajs/react"
import { useEffect } from "react"
import {
  DialogTrigger as AriaDialogTrigger,
  Heading as AriaHeading,
} from "react-aria-components"

import { Button } from "@/components/ui/button"
import { CloseButton } from "@/components/ui/close-button"
import { Input } from "@/components/ui/input"
import { Dialog, Modal, ModalOverlay } from "@/components/ui/modal"
import { RadioButton, RadioGroup } from "@/components/ui/radio-buttons"
import { adminWarmStartsPath } from "@/routes"

interface NewInviteModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

interface NewInviteForm {
  warm_start: {
    channel: string
    recipient: { name: string; email: string }
  }
}

/**
 * The "New invite" modal: collects everything required to create the warm
 * start up front (recipient + channel). Creation refusals — email already
 * claimed, already published — land back here as field errors, so the admin
 * only reaches the editor once the invite genuinely exists.
 */
export function NewInviteModal({ isOpen, onOpenChange }: NewInviteModalProps) {
  const { data, setData, post, processing, errors, clearErrors } =
    useForm<NewInviteForm>({
      warm_start: {
        channel: "link",
        recipient: { name: "", email: "" },
      },
    })

  const ws = data.warm_start
  const patchRecipient = (
    patch: Partial<NewInviteForm["warm_start"]["recipient"]>,
  ) =>
    setData("warm_start", { ...ws, recipient: { ...ws.recipient, ...patch } })

  const serverErrors = errors as Record<string, string>
  const emailError = serverErrors["new_invite.recipient_email"]
  const baseError = serverErrors["new_invite.base"]

  // A refusal redirects back to the index — make sure the modal is showing
  // the error even if it was dismissed while the request was in flight.
  useEffect(() => {
    if ((emailError || baseError) && !isOpen) onOpenChange(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailError, baseError])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    clearErrors()
    post(adminWarmStartsPath())
  }

  return (
    <AriaDialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalOverlay isDismissable>
        <Modal>
          <Dialog>
            <div className="bg-primary relative w-full overflow-hidden rounded-2xl shadow-xl sm:max-w-110">
              <CloseButton
                onClick={() => onOpenChange(false)}
                theme="light"
                size="sm"
                className="absolute top-2 right-2"
              />
              <form onSubmit={submit}>
                <div className="flex flex-col gap-0.5 px-4 pt-5 sm:px-6 sm:pt-6">
                  <AriaHeading
                    slot="title"
                    className="text-md text-primary font-semibold"
                  >
                    New invite
                  </AriaHeading>
                  <p className="text-tertiary text-sm">
                    Who&rsquo;s it for? You&rsquo;ll draft their warm start
                    playlist next.
                  </p>
                </div>

                <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6">
                  {baseError && (
                    <div className="border-error bg-error-primary text-error-primary rounded-lg border px-4 py-3 text-sm">
                      {baseError}
                    </div>
                  )}
                  <Input
                    isRequired
                    label="Their name"
                    placeholder="Maya"
                    value={ws.recipient.name}
                    onChange={(value) => patchRecipient({ name: value })}
                  />
                  <Input
                    isRequired
                    type="email"
                    label="Their email"
                    placeholder="maya@example.com"
                    value={ws.recipient.email}
                    onChange={(value) => patchRecipient({ email: value })}
                    isInvalid={!!emailError}
                    hint={emailError}
                  />
                  <RadioGroup
                    aria-label="Delivery"
                    value={ws.channel}
                    onChange={(value) =>
                      setData("warm_start", { ...ws, channel: value })
                    }
                    className="flex flex-col gap-3"
                  >
                    <RadioButton
                      value="link"
                      label="Copy a link"
                      hint="You'll get a shareable magic link to send them yourself."
                    />
                    <RadioButton
                      value="email"
                      label="Email it for me"
                      hint="Coming soon."
                      isDisabled
                    />
                  </RadioGroup>
                </div>

                <div className="flex flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pb-6">
                  <Button
                    color="secondary"
                    size="lg"
                    onClick={() => onOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    size="lg"
                    isDisabled={processing}
                    isLoading={processing}
                    showTextWhileLoading
                  >
                    Start composing
                  </Button>
                </div>
              </form>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </AriaDialogTrigger>
  )
}
